import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { NEW_RELIC_EVENT } from '@enums/new-relic.enum';
import { AdminService } from '@services/admin.service';
import { CussService } from '@services/cuss.service';
import { NewRelicEventsService, WINDOW } from '@services/new-relic-events.service';
import {
  QsiApi,
  QualtricsSurveyContext,
  QualtricsSurveyStatus,
  QualtricsWindow,
} from './qualtrics.types';

/** Margen para que el script remoto responda antes de darlo por perdido. */
const SCRIPT_TIMEOUT_MS = 5000;
/** Sondeo de `QSI.API`, que el deployment code publica después del `onload` del script. */
const API_POLL_INTERVAL_MS = 50;
const MAX_API_POLLS = 40;
/**
 * Qualtrics no expone un callback de fin de encuesta, así que la señal la manda la propia
 * encuesta con `postMessage` al padre desde su último bloque. Se aceptan las variantes más
 * usadas para no depender de una redacción exacta del lado de Qualtrics.
 */
const COMPLETION_MESSAGES = [
  'end of survey',
  'endofsurvey',
  'survey complete',
  'surveycomplete',
  'qualtricseos',
];

/**
 * Dueño único del ciclo de vida de la encuesta Qualtrics: script remoto, contenedor del
 * creative y evaluación del intercept (CI-23287).
 *
 * El kiosco no recarga nunca la página, así que el script se descarga una sola vez por sesión
 * de navegador y cada visita a la pantalla sólo repite `unload` → `load` → `run`.
 */
@Injectable({
  providedIn: 'root',
})
export class QualtricsLoaderService {
  private readonly surveyCompleted = new Subject<void>();
  /** Descarga en curso o ya resuelta del deployment code. `null` = todavía no se ha pedido. */
  private scriptLoad: Promise<void> | null = null;
  private container: HTMLElement | null = null;
  /** El intercept ya se evaluó al menos una vez en esta sesión de navegador. */
  private hasEvaluated = false;
  /** Parámetros del sondeo. Son campos y no constantes para poder acotarlos en pruebas. */
  private apiPollIntervalMs = API_POLL_INTERVAL_MS;
  private maxApiPolls = MAX_API_POLLS;
  private messageListener: ((event: MessageEvent) => void) | null = null;

  constructor(
    @Inject(WINDOW) private readonly _window: QualtricsWindow,
    private readonly _newRelicEventsService: NewRelicEventsService,
    private readonly _cussService: CussService,
    private readonly _adminService: AdminService
  ) {}

  get isMounted(): boolean {
    return !!this.container;
  }

  /** Emite cuando la encuesta avisa que el pasajero terminó de contestarla. */
  get surveyCompleted$(): Observable<void> {
    return this.surveyCompleted.asObservable();
  }

  /**
   * Monta la encuesta en la pantalla actual. Idempotente: llamarlo dos veces sin un
   * `unmount()` intermedio no hace nada.
   */
  async mount(): Promise<void> {
    if (!environment.qualtrics.enabled || this.container) {
      return;
    }

    // El kiosco pierde red con frecuencia y el script vive en un dominio externo: sin
    // conexión no se intenta, para no retrasar la pantalla final con una petición muerta.
    if (!this._window.navigator?.onLine) {
      this.track('skipped_offline');
      return;
    }

    this.container = this.createContainer();
    this._window.amKioskSurvey = this.buildContext();
    this.listenForCompletion();

    try {
      await this.ensureScript();
    } catch (error) {
      console.warn('[Qualtrics] no se pudo cargar el deployment code', error);
      this.track('load_error');
      this.unmount();
      return;
    }

    await this.evaluateIntercept();
  }

  /** Retira la encuesta y deja el DOM limpio para la siguiente sesión del kiosco. */
  unmount(): void {
    if (!this.container) {
      return;
    }

    // Sin `unload()` los creatives quedan colgados en memoria y la siguiente visita los
    // encuentra en un estado inservible; como la página no recarga, eso no se recupera solo.
    this.callQsi('unload');
    this.stopListeningForCompletion();
    this.container.remove();
    this.container = null;
    delete this._window.amKioskSurvey;
    this.removeLeftoverArtifacts();
    this.track('unmounted');
  }


  private removeLeftoverArtifacts(): void {
    const doc = this._window.document;
    for (const target of [doc?.body, doc?.documentElement]) {
      target?.style?.removeProperty('overflow');
      target?.style?.removeProperty('overflow-y');
      target?.style?.removeProperty('position');
      target?.style?.removeProperty('height');
      target?.style?.removeProperty('padding-right');
    }
  }

  /**
   * Escucha el aviso de fin de encuesta. Es un `postMessage`, que puede llegar de cualquier
   * origen, así que se descarta todo lo que no venga de Qualtrics antes de mirar el contenido.
   */
  private listenForCompletion(): void {
    if (this.messageListener) {
      return;
    }

    this.messageListener = (event: MessageEvent) => {
      if (!this.isQualtricsOrigin(event.origin) || !this.isCompletionMessage(event.data)) {
        return;
      }

      this.track('completed');
      this.surveyCompleted.next();
    };

    this._window.addEventListener('message', this.messageListener);
  }

  private stopListeningForCompletion(): void {
    if (!this.messageListener) {
      return;
    }

    this._window.removeEventListener('message', this.messageListener);
    this.messageListener = null;
  }

  /** Sólo se confía en mensajes servidos por Qualtrics sobre HTTPS. */
  private isQualtricsOrigin(origin: string): boolean {
    try {
      const { protocol, hostname } = new URL(origin);
      return (
        protocol === 'https:' &&
        (hostname === 'qualtrics.com' || hostname.endsWith('.qualtrics.com'))
      );
    } catch {
      return false;
    }
  }

  private isCompletionMessage(data: unknown): boolean {
    const matches = (value: unknown) =>
      typeof value === 'string' && COMPLETION_MESSAGES.includes(value.trim().toLowerCase());

    if (matches(data)) {
      return true;
    }

    if (data && typeof data === 'object') {
      const { type, message, event } = data as Record<string, unknown>;
      return matches(type) || matches(message) || matches(event);
    }

    return false;
  }

  /**
   * Evalúa el intercept según las dos secuencias que documenta Qualtrics para SPA:
   *
   * - Primera evaluación: basta `run()`. El `<script>` ya hizo el trabajo de `load()`.
   * - Revisitas: hay que repetir `unload()` → `load()` → `run()`, porque la página del
   *   kiosco no recarga nunca y sin eso el intercept no se volvería a evaluar.
   */
  private async evaluateIntercept(): Promise<void> {
    const api = await this.waitForApi();

    if (!api) {
      console.warn('[Qualtrics] QSI.API no disponible después de cargar el script');
      this.track('api_unavailable');
      return;
    }

    if (this.hasEvaluated) {
      this.callQsi('unload');
      this.callQsi('load');
    }

    this.callQsi('run');
    this.hasEvaluated = true;
    this.track('mounted');
  }

  /**
   * El deployment code publica `QSI.API` de forma asíncrona: cuando dispara el `onload` del
   * script todavía está descargando sus módulos (`Asset.php?Module=…`), así que consultar
   * `window.QSI` en ese instante da `undefined`. Se sondea hasta que aparezca `run`.
   */
  private waitForApi(): Promise<QsiApi | null> {
    return new Promise((resolve) => {
      let attempts = 0;

      const check = () => {
        const api = this._window.QSI?.API;
        if (api?.run) {
          resolve(api);
          return;
        }

        attempts++;
        if (attempts >= this.maxApiPolls) {
          resolve(null);
          return;
        }

        setTimeout(check, this.apiPollIntervalMs);
      };

      check();
    });
  }

  /** Toda llamada a QSI se aísla: un fallo del script de terceros no debe romper la pantalla. */
  private callQsi(method: keyof QsiApi): void {
    try {
      this._window.QSI?.API?.[method]?.();
    } catch (error) {
      console.warn(`[Qualtrics] QSI.API.${method}() falló`, error);
    }
  }

  /**
   * El contenedor vive fuera de las plantillas de Angular a propósito: si Angular lo
   * destruyera al navegar mientras QSI conserva referencias, el creative queda inservible
   * hasta reiniciar el navegador.
   */
  private createContainer(): HTMLElement {
    const doc = this._window.document;
    const existing = doc.getElementById(environment.qualtrics.zoneId);
    if (existing) {
      return existing;
    }

    const element = doc.createElement('div');
    element.id = environment.qualtrics.zoneId;
    doc.body.appendChild(element);
    return element;
  }

  private ensureScript(): Promise<void> {
    if (this.scriptLoad) {
      return this.scriptLoad;
    }

    this.scriptLoad = new Promise<void>((resolve, reject) => {
      const doc = this._window.document;
      const script = doc.createElement('script');

      /** Suelta los handlers para no retener el elemento tras el primer desenlace. */
      const cleanup = () => {
        script.onload = null;
        script.onerror = null;
      };

      const timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error(`sin respuesta tras ${SCRIPT_TIMEOUT_MS}ms`));
      }, SCRIPT_TIMEOUT_MS);

      script.type = 'text/javascript';
      script.async = true;
      script.src = environment.qualtrics.scriptUrl;
      script.onload = () => {
        clearTimeout(timeoutId);
        cleanup();
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timeoutId);
        cleanup();
        reject(new Error('el navegador no pudo cargar el script'));
      };

      doc.body.appendChild(script);
    }).catch((error: Error) => {
      // Se descarta la promesa fallida para que la siguiente visita pueda reintentar.
      this.scriptLoad = null;
      throw error;
    });

    return this.scriptLoad;
  }

  /** Metadata del Escenario 2 del CI-23287: fecha, hora, ubicación e ID de kiosco. */
  private buildContext(): QualtricsSurveyContext {
    const now = new Date();
    return {
      kioskId: this._cussService?.KioskID || '',
      location: this._adminService?.admin?.Location || '',
      date: this.formatDate(now),
      time: this.formatTime(now),
    };
  }

  /** Fecha local del kiosco en `YYYY-MM-DD`; la hora del aeropuerto es la que importa. */
  private formatDate(value: Date): string {
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }

  private formatTime(value: Date): string {
    const hours = `${value.getHours()}`.padStart(2, '0');
    const minutes = `${value.getMinutes()}`.padStart(2, '0');
    const seconds = `${value.getSeconds()}`.padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  private track(status: QualtricsSurveyStatus): void {
    this._newRelicEventsService.trackEvent(NEW_RELIC_EVENT.QUALTRICS_SURVEY, {
      status,
      kiosk_id: this._cussService?.KioskID ?? null,
      location: this._adminService?.admin?.Location ?? null,
    });
  }
}
